import {
  useRef,
  useCallback,
  useEffect,
  useEffectEvent,
  RefObject,
} from "react";
import MapView from "@arcgis/core/views/MapView";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import Graphic from "@arcgis/core/Graphic";
import {
  HighlightState,
  HoverState,
  UseWorldMapHighlightReturn,
} from "@components/world-map/types/world-map-highlight-types";
import { MapStyle } from "@components/world-map/world-map-types";
import { hitTestGraphic } from "@components/world-map/world-map-utils";
import {
  animateHighlightPop,
  findClusterGraphic,
  getFeaturePoint,
  getHighlightSymbol,
  queryClusterMemberIds,
  setHiddenHighlightFeature,
} from "@components/world-map/helpers/world-map-highlight-helpers";
import { watch } from "@arcgis/core/core/reactiveUtils";

/**
 * Highlights a single marker on the world map by overlaying a darker version
 * of the marker icon on `view.graphics`. The overlay color tracks the active
 * map style so that highlights stay readable on every basemap.
 *
 * When the target feature is hidden inside a cluster, the overlay is placed at
 * the cluster's position instead.
 *
 * Supports a persistent popup highlight via `popupEntityId`: when the caller
 * clears the hover highlight, the hook automatically falls back to highlighting
 * the popup entity.
 */
export function useWorldMapHighlight(
  viewRef: RefObject<MapView | null>,
  layerRef: RefObject<GeoJSONLayer | null>,
  isViewReady: boolean,
  mapStyle: MapStyle,
  onEntityHover?: (entityId: string | null) => void,
  popupEntityId?: string | null,
  onClusterHover?: (memberIds: readonly string[]) => void
): UseWorldMapHighlightReturn {
  const highlightState = useRef<HighlightState>({
    overlayGraphic: null,
    latestRequestId: 0,
    currentHighlightedEntityId: null,
    cancelAnimation: null,
  });
  const hoverState = useRef<HoverState>({
    isHitTestInFlight: false,
    shouldRetry: false,
    latestScreenPoint: null,
    frameHandle: null,
    lastEmittedId: null,
    pointerEntityId: null,
    lastClusterObjectId: null,
  });

  /** Entity id set via the public `highlightEntity` API (null when no external hover). */
  const hoverEntityIdRef = useRef<string | null>(null);
  const popupEntityIdRef = useRef(popupEntityId);
  popupEntityIdRef.current = popupEntityId;

  const clearOverlay = useCallback((): void => {
    const { overlayGraphic, cancelAnimation } = highlightState.current;
    if (cancelAnimation) {
      cancelAnimation();
      highlightState.current.cancelAnimation = null;
    }
    if (overlayGraphic && viewRef.current) {
      viewRef.current.graphics.remove(overlayGraphic);
    }
    highlightState.current.overlayGraphic = null;

    // Restore the underlying data-layer marker that was hidden behind the overlay.
    const view = viewRef.current;
    const layer = layerRef.current;
    if (view && layer) {
      void setHiddenHighlightFeature(view, layer, null);
    }
  }, [viewRef, layerRef]);

  const applyOverlay = useCallback(
    async (entityId: string, requestId: number): Promise<void> => {
      const view = viewRef.current;
      const layer = layerRef.current;
      if (!view || !layer) {
        return;
      }

      try {
        const geometry = await getFeaturePoint(layer, entityId);
        if (requestId !== highlightState.current.latestRequestId || !geometry) {
          return;
        }

        const clusterGraphic = await findClusterGraphic(view, layer, geometry);
        if (requestId !== highlightState.current.latestRequestId) {
          return;
        }

        const overlayPoint = clusterGraphic
          ? (clusterGraphic.geometry as __esri.Point)
          : geometry;

        // A highlight is a "transient hover" when the targeted marker is the one
        // currently under the pointer on the map and isn't the selected popup
        // entity. Those just recolor (no animation), matching the previous behavior.
        const isTransientHover =
          entityId === hoverState.current.pointerEntityId &&
          entityId !== popupEntityIdRef.current;

        const graphic = new Graphic({
          geometry: overlayPoint,
          symbol: getHighlightSymbol(
            mapStyle,
            isTransientHover ? 1 : undefined
          ),
        });
        view.graphics.add(graphic);
        highlightState.current.overlayGraphic = graphic;

        if (isTransientHover) {
          return;
        }

        // List/selected highlight → animate with a pulse in place (no vertical
        // hop), so single markers and clusters share the same motion and the
        // cluster's centered count label stays aligned with the marker.
        highlightState.current.cancelAnimation = animateHighlightPop(
          graphic,
          mapStyle
        );

        // Hide the original standalone marker so it doesn't show behind the
        // pulsing overlay. Clustered features aren't rendered individually.
        if (!clusterGraphic) {
          void setHiddenHighlightFeature(view, layer, entityId);
        }
      } catch {
        // Silently ignore — the layer or view may have been destroyed during navigation
      }
    },
    [viewRef, layerRef, mapStyle]
  );

  const setActiveHighlight = useCallback(
    (entityId: string | null): void => {
      if (entityId === highlightState.current.currentHighlightedEntityId) {
        return;
      }

      highlightState.current.currentHighlightedEntityId = entityId;
      highlightState.current.latestRequestId += 1;
      clearOverlay();

      if (!isViewReady || !entityId) {
        return;
      }

      void applyOverlay(entityId, highlightState.current.latestRequestId);
    },
    [clearOverlay, isViewReady, applyOverlay]
  );

  const highlightEntity = useCallback(
    (entityId: string | null): void => {
      hoverEntityIdRef.current = entityId;
      setActiveHighlight(entityId ?? popupEntityIdRef.current ?? null);
    },
    [setActiveHighlight]
  );

  /**
   * Syncs popup-driven highlight with map state.
   *
   * Only applies when there is no active external hover (`hoverEntityIdRef`), so popup
   * state never overrides an explicit hover coming from the list or map interactions.
   */
  useEffect(() => {
    if (hoverEntityIdRef.current) {
      return;
    }
    setActiveHighlight(popupEntityId ?? null);
  }, [popupEntityId, setActiveHighlight]);

  // Recolor the active highlight overlay when the map style changes, so the
  // overlay stays consistent with the marker color of the new basemap.
  useEffect(() => {
    const { overlayGraphic } = highlightState.current;
    if (overlayGraphic) {
      overlayGraphic.symbol = getHighlightSymbol(mapStyle);
    }
  }, [mapStyle]);

  /**
   * Recomputes the current highlight after map navigation settles.
   *
   * Cluster membership and centroid positions can change after zoom/pan, so this forces
   * a fresh overlay resolution for the currently tracked highlighted entity.
   */
  useEffect(() => {
    if (!isViewReady || !viewRef.current) {
      return;
    }

    const handle = watch(
      () => viewRef.current?.stationary,
      (isStationary?: boolean) => {
        if (!isStationary) {
          return;
        }
        const entityId = highlightState.current.currentHighlightedEntityId;
        if (!entityId) {
          return;
        }
        // Force re-computation by resetting the tracked id
        highlightState.current.currentHighlightedEntityId = null;
        setActiveHighlight(entityId);
      }
    );

    return () => handle.remove();
  }, [isViewReady, viewRef, setActiveHighlight]);

  const emitHoveredEntity = useEffectEvent((entityId: string | null): void => {
    if (entityId === hoverState.current.lastEmittedId) {
      return;
    }
    hoverState.current.lastEmittedId = entityId;
    onEntityHover?.(entityId);
  });

  /**
   * Handles pointer hover processing for map graphics.
   *
   * Flow:
   * - Runs a cluster-first hit test and renders cluster overlay when applicable.
   * - Queries cluster members and emits them through `onClusterHover`.
   * - Falls back to standalone marker hit testing and emits a single entity id.
   * - Uses in-flight/retry guards from `hoverState` to avoid overlapping async hit tests.
   */
  const handlePointerMove = useEffectEvent(async (): Promise<void> => {
    const screenPoint = hoverState.current.latestScreenPoint;
    if (!viewRef.current || !screenPoint) {
      return;
    }

    if (hoverState.current.isHitTestInFlight) {
      hoverState.current.shouldRetry = true;
      return;
    }

    hoverState.current.isHitTestInFlight = true;

    try {
      // Check cluster first — place a blue[800] pin overlay at its center.
      const clusterGraphic = await hitTestGraphic(
        viewRef.current,
        screenPoint,
        layerRef.current,
        true
      );

      if (clusterGraphic) {
        const clusterId = clusterGraphic.getObjectId();

        // Pointer is over a cluster, not a standalone marker.
        hoverState.current.pointerEntityId = null;

        // Still hovering the same cluster — skip redundant work.
        if (
          clusterId !== null &&
          clusterId !== undefined &&
          clusterId === hoverState.current.lastClusterObjectId
        ) {
          return;
        }
        hoverState.current.lastClusterObjectId = clusterId ?? null;

        const clusterPoint = clusterGraphic.geometry as __esri.Point;
        clearOverlay();
        // Direct map hover just recolors the cluster — no animation.
        const graphic = new Graphic({
          geometry: clusterPoint,
          symbol: getHighlightSymbol(mapStyle, 1),
        });
        viewRef.current.graphics.add(graphic);
        highlightState.current.overlayGraphic = graphic;
        highlightState.current.currentHighlightedEntityId = "__cluster__";

        // Mark as cluster so the next non-cluster emission won't be deduplicated.
        hoverState.current.lastEmittedId = "__cluster__";

        const memberIds = await queryClusterMemberIds(
          viewRef.current,
          layerRef.current!,
          clusterGraphic
        );
        onClusterHover?.(memberIds);
        return;
      }

      // Left a cluster — reset the dedup tracker.
      hoverState.current.lastClusterObjectId = null;

      // Check standalone marker.
      const graphic = await hitTestGraphic(
        viewRef.current,
        screenPoint,
        layerRef.current,
        false
      );
      const entityId = graphic ? String(graphic.attributes?.id) : null;

      // Track the standalone marker under the pointer so list/selected highlights
      // can be told apart from direct map hovers (which must not animate).
      hoverState.current.pointerEntityId = entityId;

      // When moving off a cluster onto empty space, clear the cluster overlay.
      if (
        !entityId &&
        highlightState.current.currentHighlightedEntityId === "__cluster__"
      ) {
        clearOverlay();
        highlightState.current.currentHighlightedEntityId = null;
      }

      emitHoveredEntity(entityId);
    } finally {
      hoverState.current.isHitTestInFlight = false;
      if (hoverState.current.shouldRetry) {
        hoverState.current.shouldRetry = false;
        void handlePointerMove();
      }
    }
  });

  const scheduleHitTest = useEffectEvent((): void => {
    if (hoverState.current.frameHandle !== null) {
      return;
    }

    hoverState.current.frameHandle = window.requestAnimationFrame(() => {
      hoverState.current.frameHandle = null;
      void handlePointerMove();
    });
  });

  /**
   * Registers pointer listeners used to drive hover hit testing.
   *
   * `pointer-move` stores the latest screen coordinates and schedules a frame-throttled
   * hit test. `pointer-leave` clears the emitted hover state.
   */
  useEffect(() => {
    if (!isViewReady || !viewRef.current || !onEntityHover) {
      return;
    }

    const hover = hoverState.current;

    const handle = viewRef.current.on("pointer-move", (event) => {
      hover.latestScreenPoint = { x: event.x, y: event.y };
      scheduleHitTest();
    });

    const leaveHandle = viewRef.current.on("pointer-leave", () => {
      hoverState.current.pointerEntityId = null;
      emitHoveredEntity(null);
    });

    return () => {
      handle.remove();
      leaveHandle.remove();
      const frameHandle = hover.frameHandle;
      if (frameHandle !== null) {
        window.cancelAnimationFrame(frameHandle);
      }
    };
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewReady, viewRef, onEntityHover]);

  /**
   * Ensures any remaining overlay graphic is removed when this hook unmounts.
   */
  useEffect(() => {
    return () => clearOverlay();
  }, [clearOverlay]);

  return { highlightEntity };
}
